import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  createChartAccount,
  deleteChartAccount,
  getChartOfAccountsSettings,
  updateChartAccount,
} from "../api/financeApi";
import { getApiErrorMessage } from "../utils/concurrency";

const ACCOUNT_TYPES = [
  { value: "bank", label: "Bank" },
  { value: "debtor_control", label: "Debtor Control" },
  { value: "creditor_control", label: "Creditor Control" },
  { value: "revenue", label: "Revenue" },
  { value: "other_income", label: "Other Income" },
  { value: "expense", label: "Expense" },
  { value: "current_asset", label: "Current Asset" },
  { value: "liability", label: "Liability" },
  { value: "equity", label: "Equity" },
  { value: "adjustment", label: "Adjustment" },
  { value: "system", label: "System" },
];

const emptyCoaForm = {
  id: null,
  code: "",
  name: "",
  account_type: "expense",
  description: "",
  is_active: true,
};

export default function ChartOfAccountsSettingsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyCoaForm);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getChartOfAccountsSettings();
      setRows(res.data.results || []);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load chart of accounts."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyCoaForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      code: row.code,
      name: row.name,
      account_type: row.account_type,
      description: row.description || "",
      is_active: row.is_active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || (!form.id && !form.code.trim())) {
      setError("Code and name are required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      if (form.id) {
        await updateChartAccount(form.id, {
          name: form.name.trim(),
          account_type: form.account_type,
          description: form.description.trim() || null,
          is_active: form.is_active,
        });
      } else {
        await createChartAccount({
          code: form.code.trim(),
          name: form.name.trim(),
          account_type: form.account_type,
          description: form.description.trim() || null,
        });
      }
      setDialogOpen(false);
      setToast({ open: true, message: "Chart account saved.", severity: "success" });
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to save chart account."));
    } finally {
      setSaving(false);
    }
  };

  const accountTypeGroups = useMemo(() => {
    const groups = ACCOUNT_TYPES.map((type) => ({
      ...type,
      accounts: rows.filter((row) => row.account_type === type.value),
    })).filter((group) => group.accounts.length > 0);
    const knownTypes = new Set(ACCOUNT_TYPES.map((type) => type.value));
    const uncategorized = rows.filter((row) => !knownTypes.has(row.account_type));
    if (uncategorized.length > 0) {
      groups.push({ label: "Other", accounts: uncategorized });
    }
    return groups;
  }, [rows]);

  const deleteAccount = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      setError("");
      await deleteChartAccount(deleteTarget.id);
      setDeleteTarget(null);
      setToast({ open: true, message: "Chart account deleted.", severity: "success" });
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to delete chart account."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Portal-wide account codes applied to every property. Changes sync to property ledgers.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Chart of accounts</Typography>
          <Button variant="contained" size="small" onClick={openCreate}>
            Add account
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4}>Loading…</TableCell>
                </TableRow>
              ) : (
                accountTypeGroups.map((group) => (
                  <React.Fragment key={group.value || group.label}>
                    <TableRow>
                      <TableCell colSpan={4} sx={{ bgcolor: "action.hover", fontWeight: 700 }}>
                        {group.label}
                      </TableCell>
                    </TableRow>
                    {group.accounts.map((row) => (
                      <TableRow key={row.id} sx={{ opacity: row.is_active ? 1 : 0.55 }}>
                        <TableCell>{row.code}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.is_active ? "Active" : "Inactive"}
                            color={row.is_active ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => openEdit(row)}
                            >
                              Edit
                            </Button>
                            {row.can_delete && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => setDeleteTarget(row)}
                              >
                                Delete
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? "Edit chart account" : "Add chart account"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            disabled={Boolean(form.id)}
            required
          />
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <FormControl>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={form.account_type}
              onChange={(e) => setForm((f) => ({ ...f, account_type: e.target.value }))}
            >
              {ACCOUNT_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            multiline
            minRows={2}
          />
          {form.id && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !saving && setDeleteTarget(null)}>
        <DialogTitle>Delete chart account?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete {deleteTarget?.code} — {deleteTarget?.name}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteAccount} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
